import { ArrowRight } from '@untitled-ui/icons-react/build/cjs';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import checkCircleIcon from 'src/assets/icons/check-circle-icon.svg';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';
import { ORDER_STATUS } from 'src/constants/common';
import { getOrgBrandingFromAppearance } from 'src/hooks/useOrgBranding';
import { store } from 'src/redux/store';
import { convertUtcToUserTimezone, formatAddress, getDateWithSuffixFormat, getShortName } from 'src/utils/CommonFunctions';

import { ITrackNavState, ITrackOrder } from './track.interface';
import { checkLocationUpdate, getDotColor, getDeliveryDateObject, getLastLocationUpdateObject, getPickupDateObject, getToFormatWiseDateTime } from './track.utils';

export { calculateTooltipPosition } from './track.utils';

/**
 * Create the large detailed popup rendered on marker hover.
 * Displays rich status label (e.g. "Picking up on Wednesday, May 6th") matching the sidebar card.
 * Uses portal API fields (ITrackOrder) — carrier.image is already a full URL.
 */
export const createTrackDetailedPopupContent = (order: ITrackOrder, navigate: (path: string, options?: { state?: ITrackNavState }) => void, PATH: Record<string, string>, timezone: string = 'UTC') => {
    const popupContainer = document.createElement('div');

    const ReactPopupContent = () => {
        const { t } = useTranslation();

        // This popup renders in its own createRoot (no redux Provider), so read the
        // appearance state straight off the store instead of via useOrgBranding.
        const orgBranding = getOrgBrandingFromAppearance(store.getState().basic.appearance);
        // Org presented as the carrier (masked or unassigned) — show the WAL icon, never initials
        const isOrgCarrier = !order.carrier?.name || order.carrier.name === orgBranding.name || order.carrier.name === 'Western Alliance';

        const isPickup = order.orderStatus === ORDER_STATUS.BOOKED || order.orderStatus === ORDER_STATUS.DISPATCHED;
        const isDelivered = order.orderStatus === ORDER_STATUS.DELIVERED;
        const isInTransit = order.orderStatus === ORDER_STATUS.IN_TRANSIT;
        const dotColor = getDotColor(order);

        const pickupDateObj = getPickupDateObject(order?.schedule, isPickup);
        const deliveryDateObj = getDeliveryDateObject(order?.schedule, isDelivered);
        const lastLocationObj = getLastLocationUpdateObject(order);

        // For in-transit: show "Picked up on [actualPickupDatetime]" when available (matches WAL behavior)
        // For delivered: show "Delivered on [actualDeliveryDatetime]" when available
        // Both use 'Do' + getDateWithSuffixFormat so the day's ordinal renders as a <sup>, the same
        // as the Overview header and the detail page's schedule cards.
        const actualPickupTime = order?.schedule?.actualPickupDatetime;
        const actualDeliveryTime = order?.schedule?.actualDeliveryDatetime;

        const checkIcon = <img src={checkCircleIcon} alt="check" className="w-4 h-4 flex-none" />;

        let statusIcon: React.ReactNode = null;
        let statusDotColor: 'primary' | 'red' | 'yellow' | 'green' | undefined = dotColor;
        let dotLabel: React.ReactNode;

        if (isInTransit && actualPickupTime) {
            const formattedTime = getDateWithSuffixFormat(`${convertUtcToUserTimezone(actualPickupTime, 'MMM Do, YYYY [at] h:mm A', true, timezone)}`);
            statusIcon = checkIcon;
            statusDotColor = undefined;
            dotLabel = <span dangerouslySetInnerHTML={{ __html: t('Radar.Picked_Up_On_Time', { time: formattedTime }) }} />;
        } else if (isDelivered && actualDeliveryTime) {
            const formattedTime = getDateWithSuffixFormat(`${convertUtcToUserTimezone(actualDeliveryTime, 'MMM Do, YYYY [at] h:mm A', true, timezone)}`);
            statusIcon = checkIcon;
            statusDotColor = undefined;
            dotLabel = <span dangerouslySetInnerHTML={{ __html: t('Radar.Delivered_On_Time', { time: formattedTime }) }} />;
        } else {
            dotLabel = (
                <>
                    <span>{isPickup ? t('Radar.Picking_Up_Status') : isDelivered ? t('Radar.Estimated_delivery') : t('Radar.Last_location_update')}</span>
                    {(isPickup || isDelivered) && (
                        <span
                            className="ml-1 italic"
                            dangerouslySetInnerHTML={{
                                __html: getToFormatWiseDateTime(isPickup ? pickupDateObj : deliveryDateObj, timezone),
                            }}
                        />
                    )}
                    {isInTransit && (
                        <span
                            className="ml-1 italic"
                            dangerouslySetInnerHTML={{
                                __html: checkLocationUpdate(lastLocationObj?.startDateTime, timezone),
                            }}
                        />
                    )}
                </>
            );
        }

        // Navigate by masterOrderId — the same key every other card/row uses. It only
        // happened to equal masterOrderAutoIncrementId on consolidated (master-mode) rows;
        // on split leg rows they differ.
        return (
            <div
                className="rounded-lg border border-gray800 bg-primarySolid p-[10px] cursor-pointer w-[327px]"
                onClick={() => navigate(`${PATH.TRACK}/${order.masterOrderId}`, { state: { orderStatus: order?.orderStatus } })}
            >
                <div className="flex gap-x-1.5">
                    <div className="mt-[2px]">
                        {order.carrier?.image ? (
                            <div className="table-profile-icn-circle-sm rounded-md !w-9 !h-9 min-w-9 min-h-9 overflow-hidden">
                                <img src={order.carrier.image} alt={order.carrier.name} className="w-full h-full object-contain" />
                            </div>
                        ) : isOrgCarrier ? (
                            // Org identity — WAL icon box (bordered so it reads on the dark popup background)
                            <div className="rounded-md !w-9 !h-9 min-w-9 min-h-9 bg-utilityBlack border border-gray800 flex items-center justify-center p-1.5">
                                <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className="table-profile-wrap notification-mark-wrap">
                                <div className="table-profile-customer-circle-sm rounded-md !w-9 !h-9 min-w-9 min-h-9">{getShortName(order.carrier.name)}</div>
                            </div>
                        )}
                    </div>

                    <div className="w-full">
                        <div className="flex justify-between items-center">
                            {/* Unassigned/masked → org name, matching the sidebar cards and table rows */}
                            <p className="text-white800 font-semibold text-sm truncate w-[180px]">{order.carrier?.name ?? orgBranding.name}</p>
                            {order.masterOrderType && (
                                <BadgeCmp style="modern" type="success" badgeTextColor="!text-utilityGray600" mainClassName="!border-utilityBlack900 text-nowrap">
                                    {order.masterOrderType.toUpperCase()}
                                </BadgeCmp>
                            )}
                        </div>
                        <p className="text-white800 font-normal text-sm truncate">{order.masterOrderFullId ? `#${order.masterOrderFullId}` : ''}</p>
                    </div>
                </div>

                <div className="mt-2">
                    <div className="flex items-center gap-2">
                        <StatusDotProfileCmp icon={statusIcon} dotColor={statusDotColor} label={dotLabel} statusLabelClassName="text-white text-xs" darkTheme={true} />
                    </div>
                </div>

                <ul className="mt-3 p-2 justify-between rounded-lg bg-white bg-opacity-10 flex items-center">
                    <li className="max-w-[135px]">
                        <div className="text-xs text-white800 font-medium truncate">
                            {order.originAddress?.city && order.originAddress?.postal
                                ? formatAddress(`${order.originAddress.city}, ${order.originAddress.postal}${order.originAddress.countryCode ? `, ${order.originAddress.countryCode}` : ''}`)
                                : ''}
                        </div>
                    </li>
                    <li>
                        <ArrowRight className="w-[14px] h-[14px] text-gray400" />
                    </li>
                    <li className="max-w-[135px]">
                        <div className="text-xs text-white800 font-medium truncate">
                            {order.destinationAddress?.city && order.destinationAddress?.postal
                                ? formatAddress(
                                      `${order.destinationAddress.city}, ${order.destinationAddress.postal}${order.destinationAddress.countryCode ? `, ${order.destinationAddress.countryCode}` : ''}`
                                  )
                                : ''}
                        </div>
                    </li>
                </ul>
            </div>
        );
    };

    const root = createRoot(popupContainer);
    root.render(<ReactPopupContent />);

    return popupContainer;
};

/**
 * Create custom marker SVG element (white pin / arrow icon).
 */
export const createMarkerSvg = () => `
    <div class="w-[12px] -mt-0.5">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="w-full">
        <path d="M12.6657 2.67916C12.2439 2.4686 11.7476 2.4686 11.3258 2.67916C10.9525 2.86552 10.7595 3.19367 10.6676 3.36083C10.5675 3.543 10.4649 3.77692 10.362 4.01162L3.05073 20.6813C2.93566 20.9436 2.82421 21.1976 2.7529 21.4084C2.69129 21.5905 2.56638 21.9837 2.70011 22.4066C2.84758 22.873 3.21319 23.2382 3.67975 23.3851C4.10282 23.5184 4.49584 23.393 4.67791 23.3312C4.88863 23.2596 5.14253 23.1478 5.4047 23.0324L11.9957 20.1324L18.5868 23.0325C18.8489 23.1478 19.1028 23.2596 19.3135 23.3312C19.4956 23.393 19.8886 23.5184 20.3117 23.3851C20.7783 23.2382 21.1439 22.873 21.2913 22.4066C21.4251 21.9837 21.3002 21.5905 21.2385 21.4084C21.1672 21.1976 21.0558 20.9436 20.9407 20.6813L13.6295 4.01167C13.5266 3.77695 13.424 3.54301 13.3238 3.36083C13.2319 3.19367 13.039 2.86552 12.6657 2.67916Z" fill="white"/>
      </svg>
    </div>`;

/**
 * Hide the small order-ID tooltip for all markers sharing the same coordinates.
 * Called when a detailed popup opens so the small label doesn't overlap it.
 */
export const hideTooltipsAtCoordinates = (coordinates: [number, number]) => {
    const allMarkers = document.querySelectorAll('.parent-marker');
    allMarkers.forEach((marker: any) => {
        const markerCoordinates = marker._coordinates;

        if (markerCoordinates && Math.abs(markerCoordinates[0] - coordinates[0]) < 0.0001 && Math.abs(markerCoordinates[1] - coordinates[1]) < 0.0001) {
            const tooltip = marker.querySelector('.order-tooltip');

            if (tooltip) tooltip.style.display = 'none';
        }
    });
};

/**
 * Restore the small order-ID tooltip for all markers at the given coordinates.
 * Called after a detailed popup closes.
 */
export const showTooltipsAtCoordinates = (coordinates: [number, number]) => {
    const allMarkers = document.querySelectorAll('.parent-marker');
    allMarkers.forEach((marker: any) => {
        const markerCoordinates = marker._coordinates;

        if (markerCoordinates && Math.abs(markerCoordinates[0] - coordinates[0]) < 0.0001 && Math.abs(markerCoordinates[1] - coordinates[1]) < 0.0001) {
            const tooltip = marker.querySelector('.order-tooltip');

            if (tooltip) tooltip.style.display = 'block';
        }
    });
};
